/* eslint-disable array-callback-return */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-param-reassign */
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { shallowEqual, useIntl, useSelector } from 'umi';
import moment from 'moment';
import CardBox from "@/pages/dashboard/components/CardBox";
import BarChart from "@/pages/dashboard/components/Chart/components/BarChart";
import LineChart from "@/pages/dashboard/components/Chart/components/LineChart";
import { addChartLabel, changeMin, xAxisHighLight } from "@/pages/dashboard/components/Chart/_config/plug";
import { FilterContext } from '@/pages/dashboard/components/PageContainer/context';
import { commonValueFormatter, formatFilterValues, formatPercentNum, formatSmallValue, unitConversion } from "@/pages/dashboard/utils/utils";
import { resolveAndSelectName, resolveConfigString } from '@/pages/dashboard/utils/metricUtils';
import dataFormat from "@home/utils/dbDataFormat/dataFormat";
import { getApiData } from '@/pages/dashboard/services/common';
import { getEchartTooltipText } from '@/pages/dashboard/components/EchartsBubble';
import { useLocal } from '@/pages/dashboard/utils/customHook';
import './index.scss';
import { cloneDeep } from 'lodash-es';

const ComparisonMetricsTrendCo = (props: any) => {
  const { className, style, config, data: propsData } = props;
  const { componentMetrics, api } = config;
  const metricsInfo = propsData?.metricsInfo;
  const contextObj = useContext(FilterContext);
  const { filterLoading, filterVal = {} } = contextObj || {};
  const { metrics = ['active_users', 'new_users'] } = filterVal || {};
  const { gameCode } = useSelector((state: any) => ({
    gameCode: state?.dashboard?.currentGame?.game_code,
  }), shallowEqual);
  const [originData, setOriginData] = useState<any>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (filterLoading) return;
    getMetricData();
  }, [filterLoading, filterVal]);

  const getMetricData = () => {
    setLoading(true);
    const { timeType, diff, rangeTime, comparisonDate, metrics, selector, ...filters } = filterVal || {};
    const params = {
      period: "daily",
      game_code: gameCode,
      comparison_date: comparisonDate,
      metrics,
      filters: formatFilterValues(filters),
    };
    // 特殊逻辑处理: 夜莺游戏日流失指标new_churn_users包含churn_level筛选器,跟产品确认先固定传255
    if (metrics?.includes('new_churn_users')) {
      params.filters = {
        ...formatFilterValues(filters),
        churn_level_list: ['255'],
      };
    }
    getApiData(api, params).then(res => {
      setOriginData(res || []);
      setLoading(false);
      setError(false);
    }).catch(() => {
      setOriginData([]);
      setLoading(false);
      setError(true);
    });
  };

  const renderMetrics = useMemo(() => 
    // 配置跟筛选器key值过滤（排除key值配置错误）
    componentMetrics?.filter((item: any) => metrics?.includes(item?.metricKey)) || []
  , [metrics, componentMetrics]);

  return <div className={className} style={style}>
    {
      renderMetrics?.map((item: any, index: number) => <ComparisonMetricsTrend
        key={index}
        originData={originData}
        metric={item}
        loading={loading}
        error={error}
        metricsInfo={metricsInfo}
      />)
    }
  </div>;
};

function renameItems(arr: { name: string; }[]) {
  const countMap: { [key: string]: number; } = {};
  const newArr  = cloneDeep(arr);
  return newArr?.map((item: { name: string; }) => {
    if (!countMap[item.name]) {
      countMap[item.name] = 1;
    }
    else {
      countMap[item.name] += 1;
      item.name = `${item.name}(${countMap[item.name]})`;
    }
    return item;
  });
}

const ComparisonMetricsTrend = (props: any) => {
  const { originData, metric, loading, error, metricsInfo } = props;
  const contextObj = useContext(FilterContext);
  const { formatMessage } = useIntl(); // 中英切换方法
  const { isEN, getLocalText } = useLocal();
  const { filterVal = {} } = contextObj || {};
  const { timeType = 'Day', comparisonDate: propsComparisonDate = [] } = filterVal || {};
  const { metricKey, formatRule: rawFormatRule, enName, cnName } = metric || {};
  const formatRule = resolveConfigString(rawFormatRule, metricsInfo);
  const isPercent = formatRule?.includes('percent') || false;
  const comparisonDate = renameItems(propsComparisonDate);
  const title = resolveAndSelectName(isEN, enName, cnName, metricsInfo) || (isEN ? enName : cnName);
  const [iseye, setIseye] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [iconType, setIconType] = useState('lineChart');
  const [showChangeTag, setShowChangeTag] = useState(false);
  const lineChartRef = useRef();
  const lineChartObj = lineChartRef?.current?.instance();

  const getVal = (value: any) => metricKey === 'arpu' && value < 1 && value > 0 ? formatSmallValue(value, 'decimals', 2) : dataFormat({ value, formatRule });
  // eslint-disable-next-line no-nested-ternary
  const handleAxisFormat = (value: moment.MomentInput) => value === '-' ? '-' : timeType === "Month" ? moment(value).format('YYYY-MM') : moment(value).format('MM-DD');

  const {
    trendList,
    maxLen,
    aggregationData,
  } = useMemo(() => {
    const trendList: any[] = [];
    const aggregationList: any[] = [];
    originData?.map((item: any) => {
      trendList?.push(item?.trend_list);
      aggregationList?.push(item?.aggregation);
    });
    // 找到最长时间补齐其他时间的长度
    const maxLen = Math.max(...trendList?.map((item: any) => item?.length));
    return {
      trendList,
      maxLen,
      aggregationData: aggregationList,
    };
  }, [originData]);

  const trendData = useMemo(() => trendList?.map((item: any) => {
    if (item?.length < maxLen) {
      const newItem = cloneDeep(item);
      for (let i = 0;i < maxLen - item?.length;i++) {
        newItem?.push({
          date: '-',
          metrics: {}
        });
      }
      return newItem;
    }
    return item;
  }), [trendList, maxLen]);

  const xAxisList = useMemo(() => trendData?.map((item) => item?.map((v: { date: string; }) => v?.date)), [trendData]);

  const formatterFunc = (params: any) => {
    const splitData = params?.map((item: any) => {
      const index = comparisonDate?.findIndex((v: any) => v?.name === item?.seriesName);
      const date = xAxisList?.[index]?.[item?.dataIndex];
      return {
        title: date === '-' ? '-' : moment(date).format(timeType === 'Month' ? 'YYYY-MM' : 'YYYY-MM-DD'),
        data: [
          {
            color: item?.color,
            name: item?.seriesName,
            metricsNum: getVal(item?.value)
          }
        ]
      };
    })?.filter((v: any) => v?.title !== '-');
    return getEchartTooltipText({
      isSplit: true,
      splitData
    });
  };

  const {
    xAxisData = [],
    legendData = [],
    barSeries = [],
    lineSeries = [],
    isNodata = true,
  } = useMemo(() => {
    let isNodata = true;
    const xAxisData: any[] = trendData?.[0]?.map((item: any) => item?.date) || [];
    const legendData: any[] = comparisonDate?.map((item: any) => item?.name);
    const barSeries: any[] = [];
    const lineSeries: any[] = [];

    legendData?.map((legend: any, index: number) => {
      const seriesObj: any = {
        name: legend,
        data: [],
      };
      const list = trendData?.[index] || [];
      list?.map((item: any) => {
        const value = item?.metrics?.[metricKey]?.value || '';
        seriesObj.data?.push(value);
        if (value) isNodata = false;
      });
      barSeries?.push({
        type: 'bar',
        barGap: '5%',
        ...seriesObj
      });
      lineSeries?.push({
        type: 'line',
        symbol: 'circle',
        lineStyle: {
          type: index === 0 ? 'solid' : 'dotted'
        },
        ...seriesObj
      });
    });

    return {
      xAxisData,
      legendData,
      barSeries,
      lineSeries,
      isNodata,
    };
  }, [trendData, isEN]);


  const summaryData = useMemo(() => {
    const summaryList: any[] = [];
    comparisonDate?.map((item: any, index: number) => {
      const list = aggregationData?.[index] || [];
      const getMetricValueByType = (list: any[], type: string) => list?.find(item => item.type === type)?.metrics?.[metricKey]?.value;
      const sumVal = getMetricValueByType(list, 'sum');
      const avgVal = getMetricValueByType(list, 'avg_no_null');
      const maxVal = getMetricValueByType(list, 'max');
      const obj = {
        id: index,
        name: item?.name,
        avg: `${getLocalText('common_Average')} : ${getVal(avgVal) || ''}`,
        sum: `${getLocalText('common_Sum')} : ${getVal(sumVal) || ''}`,
        max: `${getLocalText('common_Max')} : ${getVal(maxVal) || ''}`,
      };
      summaryList?.push(obj);
    });
    return summaryList;
  }, [aggregationData, isEN]);

  const subTitleCom = <div className='pc-dateComparison-head'>
    <div className="dateComparison-head-wrap">
      {
        summaryData?.slice(0, 3)?.map((item, index) => (
          <div key={index} className="dateComparison-head">
            <div className="dateComparison-head-title">
              <span>{item?.name}</span>
            </div>
            <div className="dateComparison-head-num">
              <span>{item?.avg}</span>
              <span>{item?.sum}</span>
              <span>{item?.max}</span>
            </div>
          </div>
        ))
      }
      {!showAll && summaryData?.length > 3 && <div className='dateComparison-head-btn' onClick={() => setShowAll(true)}>{formatMessage({ id: 'common_show_all' })}</div>}
    </div>
    {
      showAll && <div className="dateComparison-head-wrap margin-t-20">
        {
          summaryData?.slice(3, 6)?.map((item, index) => (
            <div key={index} className="dateComparison-head">
              <div className="dateComparison-head-title">
                <span>{item?.name}</span>
              </div>
              <div className="dateComparison-head-num">
                <span>{item?.avg}</span>
                <span>{item?.sum}</span>
                <span>{item?.max}</span>
              </div>
            </div>
          ))
        }
        {summaryData?.length > 3 && <div className='dateComparison-head-btn' onClick={() => setShowAll(false)}>{formatMessage({ id: 'common_show_less' })}</div>}
      </div>
    }
  </div>;

  const yAxisLabelFormat = (value: any) => {
    const formatLabel = value ? unitConversion(value, true) : value;
    const num = value < 1 && value > 0 ? parseFloat(value.toFixed(3)) : formatLabel;
    return num;
  };

  const setOtherConfigFunc = (opt: any, isLine = false) => {
    const xAxisList: any[] = [];
    trendData?.map((item: any, index) => {
      const xAxis = item?.map((v: any) => v?.date);
      if (index === 0) {
        xAxisList?.push({
          data: xAxis,
          type: 'category',
          offset: 5,
          axisTick: {
            alignWithLabel: true
          },
          axisLabel: {
            color: '#838697',
            fontSize: 10,
            formatter: (value: moment.MomentInput) => handleAxisFormat(value)
          }
        });
      } else {
        xAxisList?.push({
          data: xAxis,
          type: 'category',
          position: 'bottom',
          offset: 5 + index * 15,
          axisTick: {
            show: false,
            axisTick: {
              alignWithLabel: true
            },
          },
          axisLine: { show: false },
          axisLabel: {
            color: '#838697',
            fontSize: 10,
            formatter: (value: moment.MomentInput) => handleAxisFormat(value)
          }
        });
      }
    });
    if (trendData?.length) opt.xAxis = xAxisList;

    if (isLine) {
      opt = changeMin(opt, lineChartObj, {
        show: showChangeTag,
        showFormat: (value: any) => {
          if (!isPercent) return yAxisLabelFormat(value);
          if (value > 0 && value < 0.1 && String(value).length > 3) return formatPercentNum(+value, 2);
          return formatPercentNum(value, 1);
        }
      });
    }

    opt = addChartLabel(opt, {
      formatter: (params: any) => getVal(params?.value),
      show: iseye,
      formatNum: (value: any) => getVal(value)
    });
    opt = xAxisHighLight(opt, {
      formatter: (params: { value: string; }) => handleAxisFormat(params?.value),
      isDateComparison: true
    });
    if (isPercent) {
      opt.yAxis.axisLabel.formatter = (value: number) => formatPercentNum(value, 2);
    }
    if (metricKey === 'arpu') {
      opt.yAxis.axisLabel.formatter = (value: number) => {
        if (value < 1 && value > 0) return formatSmallValue(value, 'decimals', 2);
        return yAxisLabelFormat(value);
      };
    }
    return opt;
  };

  const renderList = [{
    type: 'eyes',
    svgType: iseye ? 'open-eyes' : 'eyes-closed',
    content: iseye ? formatMessage({ id: 'common_show_values' }) : formatMessage({ id: 'common_hide_values' }),
    isHideCom: true,
  },
  iconType === 'lineChart' && {
    type: 'turn',
    svgType: 'left-turn-right',
    content: showChangeTag ? formatMessage({ id: 'change_tag_bubble_show' }) : formatMessage({ id: 'change_tag_bubble_hidden' }),
    isHideCom: true,
  },
  {
    type: 'barChart',
    svgType: 'column-chart',
    content: formatMessage({ id: 'common_bar_chart' }),
    component: <BarChart
      height={340}
      legendData={xAxisData?.length > 0 ? legendData : []}
      xAxisData={xAxisData || []}
      series={xAxisData?.length > 0 ? barSeries : []}
      formatterFunc={formatterFunc}
      setOtherConfigFunc={(opt: any) => setOtherConfigFunc(opt)}
    />
  },
  {
    type: 'lineChart',
    svgType: 'discount-chart',
    content: formatMessage({ id: 'common_line_chart' }),
    component: <LineChart
      height={340}
      ref={lineChartRef}
      legendData={xAxisData?.length > 0 ? legendData : []}
      xAxisData={xAxisData || []}
      series={xAxisData?.length > 0 ? lineSeries : []}
      formatterFunc={formatterFunc}
      setOtherConfigFunc={(opt: any) => setOtherConfigFunc(opt, true)}
    />
  }]?.filter(v => v !== false);

  return <CardBox
    className='pc-dateComparison-card register-table'
    titleCom={title}
    subTitleCom={subTitleCom}
    height={340}
    bordered={false}
    renderList={renderList}
    defaultAvtive={3}
    setCurrentDataType={(type: any) => {
      if (type === 'eyes') {
        setIseye(!iseye);
      }
      if (type === 'turn') {
        setShowChangeTag(!showChangeTag);
      }
      if (type !== 'eyes' && type !== 'turn') {
        setIconType(type);
      }
    }}
    isLoading={loading}
    isError={error}
    isNodata={isNodata}
  />;
};

export default ComparisonMetricsTrendCo;